/**
 * AdminPartners — /admin/partners
 *
 * Lists partner applications by status (pending/approved/rejected/all).
 * Admin can approve or reject each pending application.
 * Rejection requires a reason (entered via an inline form).
 *
 * Mirrors prizes AdminPartnersPage pattern with filter tabs.
 */
import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  adminListPartners,
  adminApprovePartner,
  adminRejectPartner,
  type AdminPartnerOut,
} from '../../services/api';

interface AdminCtx {
  adminEmail: string | null;
}

const FILTERS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'All', value: '' },
] as const;

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function PartnerRow({
  partner,
  email,
  onRefresh,
}: {
  partner: AdminPartnerOut;
  email: string;
  onRefresh: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleApprove = async () => {
    setBusy(true);
    setError(null);
    try {
      await adminApprovePartner(email, partner.user_id);
      onRefresh();
    } catch {
      setError('Approval failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      setError('A rejection reason is required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await adminRejectPartner(email, partner.user_id, reason);
      setShowRejectForm(false);
      setRejectReason('');
      onRefresh();
    } catch {
      setError('Rejection failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-2">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-0.5">
          <p className="text-sm font-semibold text-gray-800">
            User ID: <span className="font-mono text-xs">{partner.user_id}</span>
          </p>
          <p className="text-xs text-gray-500">
            Contact: {partner.contact_method ?? '—'} — {partner.contact_detail ?? '—'}
          </p>
          <p className="text-xs text-gray-400">Applied: {formatDate(partner.applied_at)}</p>
          {partner.status !== 'pending' && (
            <p className="text-xs text-gray-400">
              Status: <span className="capitalize font-medium">{partner.status}</span>
              {partner.reject_reason ? ` — ${partner.reject_reason}` : ''}
            </p>
          )}
        </div>

        {partner.status === 'pending' && (
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              disabled={busy}
              onClick={handleApprove}
              className="rounded-lg bg-green-100 px-3 py-1.5 text-xs font-medium text-green-800 hover:bg-green-200 disabled:opacity-60"
            >
              Approve
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setShowRejectForm((v) => !v)}
              className="rounded-lg bg-red-100 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-200 disabled:opacity-60"
            >
              Reject
            </button>
          </div>
        )}
      </div>

      {showRejectForm && (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="text"
            placeholder="Rejection reason (required)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-violet-500 focus:outline-none"
          />
          <button
            type="button"
            disabled={busy}
            onClick={handleReject}
            className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-60"
          >
            Confirm reject
          </button>
          <button
            type="button"
            onClick={() => { setShowRejectForm(false); setRejectReason(''); }}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            Cancel
          </button>
        </div>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

export default function AdminPartners() {
  const { adminEmail } = useOutletContext<AdminCtx>();
  const [filter, setFilter] = useState<string>('pending');
  const [partners, setPartners] = useState<AdminPartnerOut[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((email: string, status: string) => {
    setLoading(true);
    setError(null);
    adminListPartners(email, status || undefined)
      .then((res) => setPartners(res.data))
      .catch(() => setError('Failed to load partner applications.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (adminEmail) load(adminEmail, filter);
  }, [adminEmail, filter, load]);

  const handleRefresh = () => {
    if (adminEmail) load(adminEmail, filter);
  };

  const emptyMsg =
    filter === 'pending'
      ? 'No pending partner applications.'
      : filter === ''
      ? 'No partner applications found.'
      : `No ${filter} partner applications.`;

  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold text-gray-900">Partner Applications</h1>

      {/* Filter tabs — mirrors prizes AdminPartnersPage */}
      <div className="mb-5 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            type="button"
            onClick={() => setFilter(f.value)}
            aria-pressed={filter === f.value}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
              filter === f.value
                ? 'bg-violet-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
      )}

      {loading && <p className="text-sm text-gray-400">Loading…</p>}

      {!loading && !error && partners.length === 0 && (
        <p className="rounded-xl border border-gray-100 bg-gray-50 py-12 text-center text-sm text-gray-400">
          {emptyMsg}
        </p>
      )}

      <div className="space-y-3">
        {partners.map((p) =>
          adminEmail ? (
            <PartnerRow key={p.id} partner={p} email={adminEmail} onRefresh={handleRefresh} />
          ) : null,
        )}
      </div>
    </div>
  );
}
